import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { DrumstickIcon, FireIcon, SparkleIcon, ChiliIcon, BurgerIcon } from '../components/Icons'
import './Home.css'

const stats = [
  { number: '13+', label: 'Years Experience' },
  { number: '1Cr+', label: 'Customers Served' },
  { number: '1000+', label: 'Team Members' },
  { number: '50+', label: 'Locations' },
]

const features = [
  {
    title: '100% Real Chicken',
    description: 'No lab-grown chicken. No shortcuts. Just pure, ethically sourced chicken.',
    icon: <DrumstickIcon size={60} />
  },
  {
    title: 'Bold Flavours',
    description: "We don't follow food trends. We make food you'll crave forever.",
    icon: <FireIcon size={60} />
  },
  {
    title: 'Fresh Daily',
    description: 'Farm to table freshness. Handcrafted cooking. Served hot & real.',
    icon: <SparkleIcon size={50} />
  },
  {
    title: 'Korean Inspired',
    description: 'Authentic Korean-style fried chicken with a modern twist.',
    icon: <ChiliIcon size={60} />
  }
]

export default function Home() {
  const rootRef = useRef(null)
  const heroRef = useRef(null)
  const statsRef = useRef(null)
  const featuresRef = useRef(null)
  const ctaRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animations
      const heroTl = gsap.timeline({ delay: 0.5 })
      
      heroTl
        .fromTo('.hero-label', 
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }
        )
        .fromTo('.hero-title .line',
          { opacity: 0, y: 100, rotateX: -90 },
          { opacity: 1, y: 0, rotateX: 0, duration: 1, stagger: 0.15, ease: 'power4.out' },
          '-=0.4'
        )
        .fromTo('.hero-description',
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' },
          '-=0.5'
        )
        .fromTo('.hero-buttons',
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' },
          '-=0.5'
        )
        .fromTo('.hero-image',
          { opacity: 0, scale: 0.8, rotate: -10 },
          { opacity: 1, scale: 1, rotate: 0, duration: 1.2, ease: 'power3.out' },
          '-=0.8'
        )
        .fromTo('.scroll-indicator',
          { opacity: 0 },
          { opacity: 1, duration: 0.5 },
          '-=0.3'
        )

      // Stats animations
      gsap.fromTo('.stat-item',
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: statsRef.current,
            start: 'top 80%',
          }
        }
      )

      // Feature cards animations
      gsap.fromTo('.feature-card',
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: featuresRef.current,
            start: 'top 75%',
          }
        }
      )

      // CTA animations
      gsap.fromTo('.cta-content > *',
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: ctaRef.current,
            start: 'top 80%',
          }
        }
      )

      // Parallax effect on hero image
      gsap.to('.hero-image', {
        y: 100,
        ease: 'none',
        scrollTrigger: {
          trigger: heroRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        }
      })

    }, rootRef)

    return () => ctx.revert()
  }, [])

  return (
    <div className="home-page" ref={rootRef}>
      {/* Hero Section */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg">
          <div className="hero-bg-pattern"></div>
        </div>
        
        <div className="hero-container container">
          <div className="hero-content">
            <span className="hero-label">India's Largest Growing QSR</span>
            
            <h1 className="hero-title">
              <span className="line">Crafting the</span>
              <span className="line">Future of</span>
              <span className="line highlight">QSR Fried Chicken</span>
            </h1>
            
            <p className="hero-description">
              Bold flavours, real chicken, and an experience that keeps you coming back. 
              Welcome to BIGGUYS — where every bite tells a story.
            </p>
            
            <div className="hero-buttons">
              {/* <Link to="/menu" className="btn btn-primary">
                Explore Menu
              </Link> */}
              <Link to="/franchise" className="btn btn-secondary">
                Own a Franchise
              </Link>
            </div>
          </div>
          
          <div className="hero-visual">
            <div className="hero-image">
              <div className="hero-image-placeholder">
                {/* <DrumstickIcon size={180} className="hero-drumstick" /> */}
                <img src="drumstick.png" height={150} className="hero-drumstick" alt="Drumstick" />
              </div>
              <div className="hero-image-accent"></div>
            </div>
          </div>
        </div>

        <div className="scroll-indicator">
          <span>Scroll to explore</span>
          <div className="scroll-line">
            <div className="scroll-dot"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section" ref={statsRef}>
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div className="stat-item" key={index}>
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" ref={featuresRef}>
        <div className="container">
          <div className="section-header">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title">The BIGGUYS Promise</h2>
            <p className="section-subtitle">
              We believe in serving food that's not just delicious, but made with integrity and passion.
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div className="feature-card" key={index}>
                <span className="feature-icon">{feature.icon}</span>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview">
        <div className="container">
          <div className="about-preview-grid">
            <div className="about-preview-content">
              <span className="section-label">Our Story</span>
              <h2 className="section-title">
                From a Dream to <br />
                <span className="highlight">India's Largest QSR</span>
              </h2>
              <p>
                What started in 2011 as a small burger joint has grown into one of India's 
                most loved QSR chains. BIGGUYS was born from the same passion and expertise, 
                bringing you the crunchiest, most flavourful fried chicken experience.
              </p>
              <p>
                Backed by Beamer Brands and sister to Biggies Burger, we're on a mission 
                to revolutionize the fried chicken game in India.
              </p>
              <Link to="/about" className="btn btn-primary">
                Learn Our Story
              </Link>
            </div>
            <div className="about-preview-image">
              <div className="image-stack">
                <div className="stack-image stack-1">
                  <BurgerIcon size={80} />
                </div>
                <div className="stack-image stack-2">
                  <DrumstickIcon size={100} />
                </div>
                <div className="stack-image stack-3">
                  <FireIcon size={70} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" ref={ctaRef}>
        <div className="container">
          <div className="cta-content">
            <span className="section-label text-white">Join the Family</span>
            <h2 className="cta-title">Ready to Own a BIGGUYS Franchise?</h2>
            <p className="cta-description">
              Join India's fastest-growing fried chicken franchise with proven profitability 
              and complete operational support.
            </p>
            <div className="cta-buttons">
              <Link to="/franchise" className="btn btn-white">
                Apply Now
              </Link>
              <Link to="/contact" className="btn btn-outline-white">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
